<script setup lang="ts">
import { computed } from 'vue'
import { RrTag, RrBadge } from '@xjlv2/admin-layout'
import {
  Bot,
  Command,
  QrCode,
  ShieldCheck,
  Sparkles
} from '@lucide/vue'
import type { Dept, ModuleMenuView, Role, TrafficWorkbenchApi } from '../../types'

const props = withDefaults(
  defineProps<{
    username: string
    moduleCount?: number
    permissionCount?: number
    canReadRbac?: boolean
    roles?: Role[]
    depts?: Dept[]
    menus?: ModuleMenuView[]
    createApiClient?: () => TrafficWorkbenchApi
    canViewTrafficAnalytics?: boolean
  }>(),
  {
    moduleCount: 0,
    permissionCount: 0,
    canReadRbac: false,
    roles: () => [],
    depts: () => [],
    menus: () => [],
    createApiClient: undefined,
    canViewTrafficAnalytics: false
  }
)

// 根据当前时间生成问候语
const greetingText = computed(() => {
  const hour = new Date().getHours()
  if (hour >= 5 && hour < 9) return '早上好'
  if (hour >= 9 && hour < 12) return '上午好'
  if (hour >= 12 && hour < 14) return '中午好'
  if (hour >= 14 && hour < 18) return '下午好'
  return '晚上好'
})

// 当前日期与星期
const formattedDate = computed(() => {
  const now = new Date()
  const weekdays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六']
  const year = now.getFullYear()
  const month = now.getMonth() + 1
  const date = now.getDate()
  const weekday = weekdays[now.getDay()]
  return `${year}年${month}月${date}日 ${weekday}`
})

// 用户首字母头像标识
const userInitial = computed(() => {
  return (props.username || 'A').slice(0, 1).toUpperCase()
})

// 角色展示：主角色 + 更多数量
const primaryRole = computed(() => {
  if (!props.roles || props.roles.length === 0) return '租户操作员'
  return props.roles[0]?.name || '租户操作员'
})

const extraRolesCount = computed(() => {
  if (!props.roles || props.roles.length <= 1) return 0
  return props.roles.length - 1
})

const allRolesTooltip = computed(() => {
  if (!props.roles || props.roles.length === 0) return ''
  return props.roles.map((r) => r.name).join('、')
})

function triggerCommandPalette() {
  window.dispatchEvent(
    new KeyboardEvent('keydown', {
      key: 'k',
      metaKey: true,
      bubbles: true
    })
  )
}
</script>

<template>
  <div class="workspace-dashboard">
    <div class="dashboard-center-container">
      <!-- 一体化沉浸式欢迎卡片 -->
      <section class="welcome-card" aria-label="工作台欢迎卡片">
        <div class="welcome-card__backdrop" aria-hidden="true">
          <div class="welcome-card__glow welcome-card__glow--primary" />
          <div class="welcome-card__glow welcome-card__glow--secondary" />
        </div>

        <div class="welcome-card__header">
          <div class="user-avatar-badge" aria-hidden="true">
            {{ userInitial }}
          </div>

          <div class="welcome-card__meta">
            <div class="welcome-card__tags" :title="allRolesTooltip">
              <span class="welcome-card__date">{{ formattedDate }}</span>
              <RrTag tone="primary">
                <ShieldCheck :size="12" aria-hidden="true" />
                {{ primaryRole }}
              </RrTag>
              <RrTag v-if="extraRolesCount > 0" tone="neutral">
                +{{ extraRolesCount }}
              </RrTag>
              <RrBadge status="success" text="服务正常" />
            </div>

            <h1 class="welcome-card__title">
              {{ greetingText }}，{{ username }}
              <Sparkles :size="24" class="sparkle-icon" aria-hidden="true" />
            </h1>
          </div>
        </div>

        <!-- 卡片内置的快捷动作栏 -->
        <div class="welcome-card__actions" aria-label="快捷入口">
          <div
            class="action-item"
            role="button"
            tabindex="0"
            @click="triggerCommandPalette"
            @keydown.enter="triggerCommandPalette"
          >
            <div class="action-item__icon action-item__icon--command" aria-hidden="true">
              <Command :size="16" />
            </div>
            <span class="action-item__label">快速导航</span>
            <span class="action-item__kbd">
              <kbd>⌘</kbd> <kbd>K</kbd>
            </span>
          </div>

          <div class="action-item">
            <div class="action-item__icon action-item__icon--agent" aria-hidden="true">
              <Bot :size="16" />
            </div>
            <span class="action-item__label">AI 智能助理</span>
            <RrTag color="success">在线</RrTag>
          </div>

          <div class="action-item">
            <div class="action-item__icon action-item__icon--wechat" aria-hidden="true">
              <QrCode :size="16" />
            </div>
            <span class="action-item__label">微信免密登录</span>
            <RrTag color="success">已就绪</RrTag>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.workspace-dashboard {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-height: 480px;
  padding: var(--rr-space-6) var(--rr-space-4);
  box-sizing: border-box;
}

.dashboard-center-container {
  width: 100%;
  max-width: 860px;
  margin-top: var(--rr-space-5);
}

/* 一体化沉浸式欢迎卡片 */
.welcome-card {
  position: relative;
  overflow: hidden;
  border-radius: var(--rr-radius-panel);
  border: 1px solid var(--rr-color-border);
  background: linear-gradient(135deg, var(--rr-color-surface) 0%, var(--rr-color-surface-subtle) 100%);
  box-shadow: var(--rr-shadow-panel);
  padding: var(--rr-space-6);
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-6);
  transition: box-shadow var(--rr-motion-duration-mid, 200ms) var(--rr-motion-ease-out, ease);
}

.welcome-card:hover {
  box-shadow: var(--rr-shadow-overlay);
}

.welcome-card__backdrop {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  overflow: hidden;
}

.welcome-card__glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.45;
}

.welcome-card__glow--primary {
  top: -60px;
  right: -20px;
  width: 280px;
  height: 280px;
  background: var(--rr-color-primary-soft);
}

.welcome-card__glow--secondary {
  bottom: -40px;
  left: 40px;
  width: 220px;
  height: 220px;
  background: var(--rr-color-surface-subtle);
}

/* 头部信息 */
.welcome-card__header {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: var(--rr-space-4);
}

.user-avatar-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: var(--rr-color-primary);
  color: var(--rr-color-surface);
  font-size: 22px;
  font-weight: 700;
  box-shadow: 0 4px 12px var(--rr-color-primary-soft);
  flex-shrink: 0;
  user-select: none;
}

.welcome-card__meta {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-2);
  min-width: 0;
}

.welcome-card__tags {
  display: flex;
  align-items: center;
  gap: var(--rr-space-2);
  flex-wrap: wrap;
}

.welcome-card__date {
  font-size: var(--rr-font-size-xs, 12px);
  color: var(--rr-color-text-muted);
  font-weight: 500;
}

.welcome-card__title {
  display: flex;
  align-items: center;
  gap: var(--rr-space-2);
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  color: var(--rr-color-text-strong);
  letter-spacing: -0.01em;
  line-height: 1.25;
}

.sparkle-icon {
  color: var(--rr-color-primary);
}

/* 内置快捷动作栏 */
.welcome-card__actions {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--rr-space-3);
  padding-top: var(--rr-space-5);
  border-top: 1px solid var(--rr-color-border);
}

.action-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--rr-space-2) var(--rr-space-3);
  background: var(--rr-color-surface);
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-control);
  cursor: pointer;
  outline: 0;
  transition:
    transform var(--rr-motion-duration-fast, 150ms) var(--rr-motion-ease-out, ease),
    border-color var(--rr-motion-duration-fast, 150ms) var(--rr-motion-ease-out, ease),
    box-shadow var(--rr-motion-duration-fast, 150ms) var(--rr-motion-ease-out, ease);
}

.action-item:hover,
.action-item:focus-visible {
  transform: translateY(-2px);
  border-color: var(--rr-color-primary-border);
  box-shadow: var(--rr-shadow-panel);
}

.action-item__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: var(--rr-radius-control);
  flex-shrink: 0;
  margin-right: var(--rr-space-2);
}

.action-item__icon--command {
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text);
  border: 1px solid var(--rr-color-border);
}

.action-item__icon--agent {
  background: var(--rr-color-primary-soft);
  color: var(--rr-color-primary);
}

.action-item__icon--wechat {
  background: var(--rr-color-success-soft);
  color: var(--rr-color-success);
}

.action-item__label {
  flex: 1;
  font-size: var(--rr-font-size-base, 13px);
  font-weight: 600;
  color: var(--rr-color-text-strong);
  white-space: nowrap;
}

.action-item__kbd {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

kbd {
  display: inline-block;
  padding: 1px 5px;
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  color: var(--rr-color-text-muted);
  background: var(--rr-color-surface-subtle);
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-control);
  line-height: 1.3;
}

@media (max-width: 768px) {
  .dashboard-center-container {
    margin-top: var(--rr-space-2);
  }

  .welcome-card {
    padding: var(--rr-space-4);
    gap: var(--rr-space-4);
  }

  .welcome-card__title {
    font-size: 20px;
  }

  .welcome-card__actions {
    grid-template-columns: 1fr;
    padding-top: var(--rr-space-3);
  }
}
</style>
